'use strict';

import {Slide} from "./Slide";
import {isSafari, projectMode, settingsData} from "../../settings";
import {addTilesToScene, envGroup} from "../threejs/environment";
import {getCookie} from "../cookies/setAndGetCookies";
import {setLordIcon} from "../../lordicon";
import {muteToggle} from "../audio/audio";

export class Slider {
    #element = document.createElement('div');;
    #slides = null;
    #videoElement = null;
    #videoParams = null;
    #musicElement = null;
    #musicParams = null;
    #controlPanel = null;
    #skipButton = null;
    #muteButton = null;
    #muteIcon = null;
    #muteText = null;
    #removeIntervalId = null;
    #selfdestructerId = null;
    #kenburnsDirections = [
        'top', 'bottom', 'left', 'right'
    ];
    #baseDelay = 0.5;
    #animationDurations = {
        duration: 6,
        duration2: 2,
    };

    constructor(parent, slides, videoParams, musicParams) {
        this.#element.id = 'preloader_slider';
        this.#slides = slides;
        this.#videoParams = {
            url: videoParams.url,
            position: videoParams.position,
            extension: videoParams.extension,
            muted: true,
            autoplay: true,
            playsInline: true,
        };
        this.#musicParams = {
            url: musicParams.url,
            extension: musicParams.extension,
            muted: true,
            autoplay: false,
            loop: true,
            playsInline: true,
        };

        this.#selfdestructerId = setInterval(() => {
            if (settingsData.slideShowed && this.#element) this.remove();
        }, 1000);

        parent.append(this.#element);
    }

    launch() {
        this.#createControlPanel();
        Slider.#showElement(this.#controlPanel);
        this.#appendMusic();

        if (this.#videoParams.position === 'start' || this.#slides.length === 0) {
            this.#appendVideo();
            this.#videoElement.addEventListener('ended', () => {
                this.#removeVideo();
                this.#launchSlides();
                this.#terminationPhase();
            }, false);
        } else {
            this.#launchSlides();
            this.#terminationPhase();
        }
    }

    remove() {
        this.#pauseActiveElement();
        Slider.#disableElement(this.#skipButton);
        clearInterval(this.#removeIntervalId);
        clearInterval(this.#selfdestructerId);
        if (projectMode.LiteMode.isActive()) this.#musicElement.pause();
        settingsData.slideShowed = true;
        addTilesToScene().then(() =>{
            const imageCheck = document.getElementById('image-check');
            imageCheck && imageCheck.remove();
            this.#element && this.#element.remove();
        });
    }

    #pauseActiveElement() {
        this.#videoElement && this.#videoElement.pause();

        if (this.#musicElement) {
            const intervalId = setInterval(() => {
                // if (settingsData.flyIsComplete) {
                //     clearInterval(intervalId);
                //     this.#musicElement.remove();
                //     return;
                // }
                if (this.#musicElement.volume <= 0.05) {
                    clearInterval(intervalId);
                    this.#musicElement.remove();
                    return;
                }
                this.#musicElement.volume -= 0.05;
            }, (settingsData.endTime / 20) * 1000);
        }
    }

    #launchSlides() {
        if (this.#slides.length === 0) this.remove();

        this.#calculateAnimationDuration();

        for (let i = 0; i < this.#slides.length; i++) {
            const kenburnsDirection = this.#kenburnsDirections.shift();
            this.#kenburnsDirections.push(kenburnsDirection);

            let isReverse = 'reverse';
            if (i % 2 !== 0)
                isReverse = '';

            new Slide(
                this.#slides[i].imageUrl,
                this.#getSlogan(i),
                this.#element,
                {
                    zIndex: this.#slides.length - i,
                    imageAnimation: `kenburns-${kenburnsDirection} ${this.#animationDurations.duration}s linear both ${isReverse} ${this.#animationDurations[`delay${i + 1}`]}s, fade-out ${this.#animationDurations.duration2}s ease-in forwards ${this.#animationDurations[`delay${i + 2}`]}s`,
                    titleAnimation: `focus-in-contract 1.3s cubic-bezier(0.250, 0.460, 0.450, 0.940) forwards ${this.#animationDurations[`titleDelay${i + 1}`]}s, text-blur-out 1.2s ease-in forwards ${this.#animationDurations[`delay${i + 2}`]}s`,
                }
            );
        }
    }

    #terminationPhase() {
        this.#removeIntervalId = setInterval(() => {
            if (this.#videoParams.position === 'end') {
                clearInterval(this.#removeIntervalId);
                this.#appendVideo();
                this.#videoElement.addEventListener('ended', () => {
                    this.#removeVideo();
                    this.remove();
                }, false);
            } else {
                this.remove();
            }
        }, 5 * this.#slides.length * 1000);
    }

    #calculateAnimationDuration() {
        for (let i = 1; i <= this.#slides.length + 1; i++) {
            this.#animationDurations[`delay${i}`] = this.#delay(i);
            this.#animationDurations[`titleDelay${i}`] = this.#delay(i) + 1.3;
        }
    }

    #delay(n) {
        return this.#baseDelay + this.#baseDelay * 10 * (n - 1);
    }

    #createVideo() {
        if (!this.#videoParams.url) return;
        if (this.#musicParams.url) this.#videoParams.muted = true;

        this.#videoElement = document.createElement('video');
        this.#videoElement.autoplay = this.#videoParams.autoplay;
        this.#videoElement.muted = this.#videoParams.muted;
        this.#videoElement.playsInline = this.#videoParams.playsInline;
        this.#videoElement.classList.add('preloader-video');
        const source = document.createElement('source');
        source.src = this.#videoParams.url;
        source.type = `video/${this.#videoParams.extension}`;
        this.#videoElement.appendChild(source);
    }

    #removeVideo() {
        if (!this.#videoElement) return;

        this.#videoElement.remove();
        this.#videoElement = null;

        if (!this.#musicElement) Slider.#hideElement(this.#muteButton);
    }

    #appendVideo() {
        this.#createVideo();

        if (!this.#videoElement) return;

        this.#element.appendChild(this.#videoElement);
        Slider.#showElement(this.#muteButton);
    }

    #createMusic() {
        if (!this.#musicParams.url) return;

        this.#musicElement = document.createElement('audio');
        this.#musicElement.muted = this.#musicParams.muted;
        this.#musicElement.loop = this.#musicParams.loop;
        this.#musicElement.classList.add('background-music');
        const source = document.createElement('source');
        source.src = this.#musicParams.url;
        source.type = `audio/${this.#musicParams.extension}`;
        this.#musicElement.appendChild(source);
    }

    #removeMusic() {
        if (!this.#musicElement) return;

        this.#musicElement.remove();
        this.#musicElement = null;
    }

    #appendMusic() {
        this.#createMusic();

        if (!this.#musicElement) return;

        document.body.appendChild(this.#musicElement);
        Slider.#showElement(this.#muteButton);
    }

    #getSlogan(counter) {
        let slogan = this.#slides[counter].slogan;
        const userLanguage = getCookie('language');
        const slogans = this.#slides[counter]?.slogans;

        slogans && slogans.forEach(elem => {
            if (elem.lang === userLanguage && elem.value)
                slogan = elem.value;
        });

        return slogan;
    }

    #createControlPanel() {
        this.#controlPanel = document.createElement('div');
        this.#controlPanel.classList.add('control-panel', 'hidden');
        isSafari && this.#controlPanel.classList.add('control-panel__safari-styles');
        this.#createSkipButton();
        this.#createMuteButton();
        this.#controlPanel.appendChild(this.#skipButton);
        this.#controlPanel.appendChild(this.#muteButton);
        this.#element.appendChild(this.#controlPanel);
    }

    #createSkipButton() {
        this.#skipButton = document.createElement('button');
        this.#skipButton.classList.add('control-panel__button', 'control-button', 'skip', 'language-string');
        this.#skipButton.setAttribute('data-dictionary', 'Skip intro');
        this.#skipButton.innerText = Slider.#getTranslation('Skip intro');
        this.#skipButton.addEventListener('click', () => this.remove());
    }

    #createMuteButton() {
        this.#muteButton = document.createElement('div');
        this.#muteButton.classList.add(
            'control-button',
            'control-panel__button',
            'mute',
            'hidden'
        );
        this.#appendMuteIcon();
        this.#appendMuteText();
        this.#muteButton.onclick = () => this.#muteButtonHandler();
    }

    #appendMuteIcon() {
        this.#muteIcon = document.createElement('span');
        this.#muteIcon.classList.add(
            'mute__icon',
            'aside__sound__btn',
            'aside__bottom__btn'
        );
        setLordIcon('soundOn',this.#muteIcon,{
            trigger: 'morph',
            delay:'0',
            primaryColor: '#ffffff',
            secondaryColor: '#ffffff',
        });
        setLordIcon('soundOff',this.#muteIcon,{
            trigger: 'morph',
            delay:'0',
            primaryColor: '#ffffff',
            secondaryColor: '#ffffff',
        });
        this.#muteIcon.dataset.active = 'on';
        this.#muteButton.appendChild(this.#muteIcon);
    }

    #appendMuteText() {
        this.#muteText = document.createElement('span');
        this.#muteText.classList.add('mute__text', 'language-string');
        this.#skipButton.setAttribute('data-dictionary', 'Unmute');
        this.#muteText.innerHTML = Slider.#getTranslation('Unmute');
        this.#muteButton.appendChild(this.#muteText);
    }

    #muteButtonHandler() {
        this.#toggleSound();
        this.#toggleMuteIcon();
        this.#toggleMuteText();
    }

    #toggleSound() {
        if (this.#musicElement) {
            this.#musicElement.muted = !this.#musicElement.muted;
            this.#musicElement.muted ?
                this.#musicElement.pause() :
                this.#musicElement.play();
        } else if (this.#videoElement) {
            this.#videoElement.muted = !this.#videoElement.muted;
        }
    }

    #toggleMuteIcon() {
        let action;

        this.#muteIcon.dataset.active === 'on' ?
            action = 'off' :
            action = 'on';

        this.#muteIcon.dataset.active = action;
        for (let child of this.#muteIcon.children){
            child.connectedTrigger.leave();
        }
    }

    #toggleMuteText() {
        let action = 'Mute';

        if (this.#muteText.innerHTML === 'Mute') action = 'Unmute';

        this.#muteText.setAttribute('data-dictionary', action);
        this.#muteText.innerHTML = Slider.#getTranslation(action);
    }

    static #getTranslation(text) {
        let translation = text;
        let language = getCookie('language') || 'en';
        if (config.dictionary.hasOwnProperty(text))
            translation = config.dictionary[text][language];

        return translation;
    }

    static #hideElement(element) {
        element && element.classList.add('hidden');
    }

    static #showElement(element) {
        element && element.classList.remove('hidden');
    }

    static #disableElement(element) {
        element && (element.disabled = true);
    }
}
